import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15, default as _styled16, default as _styled17, default as _styled18, default as _styled19, default as _styled20, default as _styled21, default as _styled22, default as _styled23, default as _styled24, default as _styled25, default as _styled26 } from "styled-components";
import React, { useRef } from "react";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { takeFirst } from "~/features/app/utils";
import { Media } from "~/features/ui/media";
import { Headline } from "~/features/ui/headline";
import { Button } from "~/features/ui/button";
import { RichText } from "~/features/ui/rich-text";
import { Grid } from "~/features/ui/grid";
import { Link } from "~/features/ui/link";
import { getThemeStyle } from "~/features/ui/get-theme-style";
import { debounce } from "~/features/app/utils";
import { VideoControls } from "~/features/ui/video/video-controls";
import { ArticleTeaser } from "~/features/hero/complex-hero/article-teaser";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { up } from "~/features/app/screens";
import { useLinkIcon } from "~/features/ui/link/use-link-icon";
import { isLinkValid } from "~/features/ui/link/utils";
import { useNavLayout } from "~/features/app/app-header-v2/context";
import { Wrap } from "~/features/ui/wrap";
const fragment = graphql(`
  fragment ComplexHero on hero_complexHero_BlockType {
    headline: baseHeadline
    media: baseMedia {
      ...Media
    }
    caption: baseText
    articles: articleTeasers {
      ...ArticleTeaser
    }
    bottom: children {
      ... on hero_complexHeroBottom_BlockType {
        title: baseHeadline
        text: baseRichText
        link: baseLink {
          ...Link
        }
        media: baseMedia {
          ...Media
        }
      }
    }
  }
`);
const containerStyle = {
  "display": "flex",
  "flexDirection": "column",
  "alignItems": "center",
  "borderBottomWidth": "1px",
  "&::before": {
    "content": "var(--tw-content)",
    "position": "absolute",
    "left": "0",
    "right": "0",
    "top": "0",
    "zIndex": "10",
    "height": "50%",
    "backgroundImage": "linear-gradient(180deg, rgba(0, 0, 0, 0.80) 0%, rgba(0, 0, 0, 0.00) 100%)",
    "--tw-content": "\"\""
  },
  "&::after": {
    "content": "var(--tw-content)",
    "position": "absolute",
    "left": "0",
    "right": "0",
    "bottom": "0",
    "height": "50%",
    "backgroundImage": "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, #000 100%)",
    "--tw-content": "\"\""
  }
};
export function ComplexHero(props: FragmentType<typeof fragment>) {
  const {
    headline,
    media: fullscreenMedia,
    caption,
    bottom,
    articles = []
  } = useFragment(fragment, props);
  const bottomData = takeFirst(bottom);
  const hasBottomData = bottomData && "title" in bottomData;
  const title = hasBottomData ? bottomData.title : undefined;
  const link = hasBottomData ? bottomData.link : undefined;
  const bottomMedia = hasBottomData ? bottomData.media : undefined;
  const text = hasBottomData ? bottomData.text : undefined;
  const blurRef = React.useRef<HTMLDivElement>(null);
  const topGridRef = React.useRef<HTMLDivElement>(null);
  const bottomGridRef = React.useRef<HTMLDivElement>(null);
  const rightImageRef = React.useRef<HTMLDivElement>(null);
  const fullscreenVideoRef = useRef<HTMLVideoElement>(null);
  const bottomVideoRef = useRef<HTMLVideoElement>(null);
  const darkStyle = getThemeStyle({
    mode: "dark"
  });
  const lightStyle = getThemeStyle({
    mode: "light"
  });
  const cta = takeFirst(link);
  const icon = useLinkIcon(cta || undefined);
  const isLg = useMediaQuery(up("lg"));
  const {
    isAnnouncementBarVisible
  } = useNavLayout();
  const topSpacing = isAnnouncementBarVisible ? {
    "marginTop": "calc(var(--header-height) + var(--announcement-bar-height) + 56px)",
    "@media (min-width: 1024px)": {
      "marginTop": "calc(var(--header-height) + var(--announcement-bar-height))"
    }
  } : {
    "marginTop": "calc(var(--header-height) + 56px)",
    "@media (min-width: 1024px)": {
      "marginTop": "var(--header-height)"
    }
  };
  React.useEffect(() => {
    if (!blurRef.current) {
      return;
    }
    const onScroll = () => {
      const blurValue = Math.min(8, window.scrollY / 100);
      const filterValue = `blur(${blurValue}px)`;
      blurRef.current!.style.backdropFilter = filterValue;
      // @ts-expect-error
      blurRef.current!.style.webkitBackdropFilter = filterValue;
    };
    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);
  React.useEffect(() => {
    if (!isLg || !rightImageRef.current || !topGridRef.current || !bottomGridRef.current) {
      return;
    }
    function onResize() {
      const topGridHeight = topGridRef.current?.clientHeight || 0;
      const bottomGridHeight = bottomGridRef.current?.clientHeight || 0;
      const height = topGridHeight + bottomGridHeight;
      rightImageRef.current!.style.height = `${height - 160}px`;
    }
    onResize();
    const debouncedOnResize = debounce(onResize);
    window.addEventListener("resize", debouncedOnResize);
    return () => {
      window.removeEventListener("resize", debouncedOnResize);
    };
  }, [isLg]);
  return <_StyledDiv $_css={[{
    "borderTopWidth": "0px !important"
  }, lightStyle.background]}>
      <_StyledDiv2 $_css2={[{
      "position": "relative",
      "zIndex": "10",
      "paddingBottom": "12rem",
      "@media (min-width: 1024px)": {
        "paddingBottom": "0"
      }
    }, darkStyle.text]}>
        <_StyledDiv3 ref={blurRef} />
        <_StyledDiv4 $_css3={[{
        "minHeight": "100svh"
      }, containerStyle, darkStyle.border]}>
          <_StyledDiv5>
            <Media {...takeFirst(fullscreenMedia)} imageProps={{
            fit: "cover",
            rounded: false,
            loading: "eager",
            priority: true
          }} inlineVideoProps={{
            fit: "cover",
            videoRef: fullscreenVideoRef,
            rounded: false,
            priority: true
          }} externalVideoProps={{
            fit: "cover",
            rounded: false
          }} />
          </_StyledDiv5>
          <_StyledDiv6 $_css4={[{
          "position": "relative",
          "zIndex": "10",
          "display": "flex",
          "flex": "1 1 0%",
          "alignItems": "center",
          "paddingLeft": "1.6rem",
          "paddingRight": "1.6rem",
          "paddingTop": "8rem",
          "paddingBottom": "8rem"
        }, topSpacing]}>
            {headline && <_StyledHeadline text={headline} />}
          </_StyledDiv6>
          <_StyledDiv7>
            <_StyledDiv8>
              <VideoControls videoRef={fullscreenVideoRef} />
            </_StyledDiv8>
          </_StyledDiv7>
          <_StyledGrid $_css5={[{
          "position": "relative",
          "zIndex": "10",
          "borderBottomWidth": "1px",
          "@media (min-width: 1024px)": {
            "marginTop": "auto"
          }
        }, darkStyle.border]}>
            <_StyledDiv9 $_css6={[{
            "gridColumn": "1 / -1",
            "> :not([hidden]) ~ :not([hidden])": {
              "--tw-divide-y-reverse": "0",
              "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
              "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
            },
            "borderTopWidth": "1px",
            "@media (min-width: 1024px)": {
              "gridColumn": "span 8 / span 8",
              "gridColumnStart": "5",
              "borderLeftWidth": "1px"
            }
          }, darkStyle.border]}>
              {caption && <_StyledDiv10>
                  <p>{caption}</p>
                </_StyledDiv10>}
              <_StyledDiv11 $_css7={[{
              "display": "flex",
              "flexDirection": "column",
              "> :not([hidden]) ~ :not([hidden])": {
                "--tw-divide-y-reverse": "0",
                "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
                "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
              },
              "backgroundColor": "rgba(0, 7, 4, 0.1)",
              "--tw-backdrop-blur": "blur(8px)",
              "backdropFilter": "var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia)",
              "@media (min-width: 1024px)": {
                "flexDirection": "row",
                "> :not([hidden]) ~ :not([hidden])": {
                  "--tw-divide-x-reverse": "0",
                  "borderRightWidth": "calc(1px * var(--tw-divide-x-reverse))",
                  "borderLeftWidth": "calc(1px * calc(1 - var(--tw-divide-x-reverse)))",
                  "--tw-divide-y-reverse": "0",
                  "borderTopWidth": "calc(0px * calc(1 - var(--tw-divide-y-reverse)))",
                  "borderBottomWidth": "calc(0px * var(--tw-divide-y-reverse))"
                }
              }
            }, darkStyle.border]}>
                {articles.map((article, idx) => <ArticleTeaser key={idx} {...article} />)}
                <_StyledDiv12>
                  <VideoControls size="large" videoRef={fullscreenVideoRef} />
                </_StyledDiv12>
              </_StyledDiv11>
            </_StyledDiv9>
          </_StyledGrid>
        </_StyledDiv4>
        <Wrap mode="dark">
          <_StyledGrid2 ref={topGridRef}>
            <_StyledDiv13>
              {title && <_StyledHeadline2 type="h2" text={title} />}
              {isLinkValid(link) && <_StyledDiv14>
                  <Button as={Link} variant="secondary" {...cta} icon={icon} />
                </_StyledDiv14>}
            </_StyledDiv13>
            <_StyledDiv15>
              <_StyledDiv16 ref={rightImageRef}>
                <Media {...takeFirst(bottomMedia)} imageProps={{
                fit: "cover",
                sizes: "lg:50vw"
              }} inlineVideoProps={{
                fit: "cover",
                videoRef: bottomVideoRef,
                controls: <_StyledVideoControls videoRef={bottomVideoRef} />
              }} externalVideoProps={{
                fit: "cover"
              }} />
              </_StyledDiv16>
            </_StyledDiv15>
          </_StyledGrid2>
        </Wrap>
      </_StyledDiv2>
      <_StyledDiv17>
        <Media {...takeFirst(bottomMedia)} imageProps={{
        ratio: "4/3",
        sizes: "lg:50vw"
      }} inlineVideoProps={{
        ratio: "4/3",
        videoRef: bottomVideoRef,
        controls: <_StyledVideoControls2 videoRef={bottomVideoRef} />
      }} externalVideoProps={{
        ratio: "4/3"
      }} />
      </_StyledDiv17>
      <Wrap mode="light">
        <_StyledGrid3 ref={bottomGridRef}>
          <_StyledDiv18>
            {text && <RichText text={text} />}
            {isLinkValid(link) && <_StyledDiv19>
                <Button as={Link} variant="secondary" {...cta} icon={icon} />
              </_StyledDiv19>}
          </_StyledDiv18>
        </_StyledGrid3>
      </Wrap>
    </_StyledDiv>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-y9t5fl-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-y9t5fl-1"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-y9t5fl-2"
})({
  "position": "absolute",
  "inset": "0",
  "zIndex": "10"
});
var _StyledDiv4 = _styled4("div").withConfig({
  componentId: "sc-sf-y9t5fl-3"
})`${p => p.$_css3}`;
var _StyledDiv5 = _styled5("div").withConfig({
  componentId: "sc-sf-y9t5fl-4"
})({
  "position": "absolute",
  "inset": "0"
});
var _StyledDiv6 = _styled6("div").withConfig({
  componentId: "sc-sf-y9t5fl-5"
})`${p => p.$_css4}`;
var _StyledHeadline = _styled7(Headline).withConfig({
  componentId: "sc-sf-y9t5fl-6"
})({
  "textAlign": "center",
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "4.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.044rem",
    "fontWeight": "400"
  }
});
var _StyledDiv7 = _styled8("div").withConfig({
  componentId: "sc-sf-y9t5fl-7"
})({
  "position": "relative",
  "zIndex": "10",
  "marginLeft": "auto",
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});
var _StyledDiv8 = _styled9("div").withConfig({
  componentId: "sc-sf-y9t5fl-8"
})({
  "paddingBottom": "1.6rem",
  "paddingRight": "1.6rem"
});
var _StyledGrid = _styled10(Grid).withConfig({
  componentId: "sc-sf-y9t5fl-9"
})`${p => p.$_css5}`;
var _StyledDiv9 = _styled11("div").withConfig({
  componentId: "sc-sf-y9t5fl-10"
})`${p => p.$_css6}`;
var _StyledDiv10 = _styled12("div").withConfig({
  componentId: "sc-sf-y9t5fl-11"
})({
  "width": "100%",
  "paddingLeft": "4rem",
  "paddingRight": "4rem",
  "paddingTop": "2.4rem",
  "paddingBottom": "2.4rem",
  "@media (min-width: 1024px)": {
    "paddingLeft": "2.4rem",
    "paddingRight": "2.4rem"
  }
});
var _StyledDiv11 = _styled13("div").withConfig({
  componentId: "sc-sf-y9t5fl-12"
})`${p => p.$_css7}`;
var _StyledDiv12 = _styled14("div").withConfig({
  componentId: "sc-sf-y9t5fl-13"
})({
  "display": "none",
  "@media (min-width: 1024px)": {
    "display": "block"
  }
});
var _StyledGrid2 = _styled15(Grid).withConfig({
  componentId: "sc-sf-y9t5fl-14"
})({
  "position": "relative",
  "zIndex": "10",
  "padding": "4rem",
  "@media (min-width: 1024px)": {
    "padding": "8rem"
  }
});
var _StyledDiv13 = _styled16("div").withConfig({
  componentId: "sc-sf-y9t5fl-15"
})({
  "gridColumn": "1 / -1",
  "display": "flex",
  "flexDirection": "column",
  "rowGap": "2.4rem",
  "@media (min-width: 1024px)": {
    "gridColumn": "span 6 / span 6",
    "rowGap": "4rem",
    "paddingRight": "8rem"
  }
});
var _StyledHeadline2 = _styled17(Headline).withConfig({
  componentId: "sc-sf-y9t5fl-16"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledDiv14 = _styled18("div").withConfig({
  componentId: "sc-sf-y9t5fl-17"
})({
  "display": "none",
  "@media (min-width: 1024px)": {
    "display": "block"
  }
});
var _StyledDiv15 = _styled19("div").withConfig({
  componentId: "sc-sf-y9t5fl-18"
})({
  "position": "relative",
  "gridColumn": "span 6 / span 6",
  "display": "none",
  "@media (min-width: 1024px)": {
    "display": "block"
  }
});
var _StyledDiv16 = _styled20("div").withConfig({
  componentId: "sc-sf-y9t5fl-19"
})({
  "position": "absolute",
  "left": "0",
  "right": "0"
});
var _StyledVideoControls = _styled21(VideoControls).withConfig({
  componentId: "sc-sf-y9t5fl-20"
})({
  "position": "absolute",
  "right": "1.6rem",
  "top": "1.6rem",
  "zIndex": "10",
  "@media (min-width: 1024px)": {
    "right": "2.4rem",
    "top": "2.4rem"
  }
});
var _StyledDiv17 = _styled22("div").withConfig({
  componentId: "sc-sf-y9t5fl-21"
})({
  "position": "relative",
  "zIndex": "10",
  "marginTop": "-12rem",
  "paddingLeft": "1.6rem",
  "paddingRight": "1.6rem",
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});
var _StyledVideoControls2 = _styled23(VideoControls).withConfig({
  componentId: "sc-sf-y9t5fl-22"
})({
  "position": "absolute",
  "right": "1.6rem",
  "top": "1.6rem",
  "zIndex": "10",
  "@media (min-width: 1024px)": {
    "right": "2.4rem",
    "top": "2.4rem"
  }
});
var _StyledGrid3 = _styled24(Grid).withConfig({
  componentId: "sc-sf-y9t5fl-23"
})({
  "paddingLeft": "4rem",
  "paddingRight": "4rem",
  "paddingBottom": "4rem",
  "paddingTop": "2.4rem",
  "@media (min-width: 1024px)": {
    "padding": "8rem"
  }
});
var _StyledDiv18 = _styled25("div").withConfig({
  componentId: "sc-sf-y9t5fl-24"
})({
  "position": "relative",
  "zIndex": "10",
  "gridColumn": "1 / -1",
  "display": "flex",
  "flexDirection": "column",
  "rowGap": "2.4rem",
  "@media (min-width: 1024px)": {
    "gridColumn": "span 6 / span 6",
    "paddingRight": "8rem"
  }
});
var _StyledDiv19 = /*#__PURE__*/_styled26("div").withConfig({
  displayName: "complex-hero___StyledDiv19",
  componentId: "sc-8uying-0"
})({
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});